import React, { useState, useEffect, useCallback, useRef } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { ToastProvider, useToast } from './components/ui/Toast';
import { CommercialPAWorkbench } from './components/LDU/CommercialPAWorkbench';
import { PolicySchedule } from './components/LDU/PolicySchedule';
import { AdminModule } from './components/LDU/AdminModule';
import { QuoteHistory } from './components/LDU/QuoteHistory';
import { LoginScreen } from './components/LDU/LoginScreen';
import { CloudflareLoginScreen } from './components/LDU/CloudflareLoginScreen';
import { AppliedFacilityCard } from './components/LDU/AppliedFacilityCard';
import { BindQuotePanel } from './components/LDU/BindQuotePanel';
import { ReferQuotePanel } from './components/LDU/ReferQuotePanel';
import { ModulePlaceholder } from './components/LDU/ModulePlaceholder';
import { Sidebar } from './components/ui/Sidebar';
import {
  type CurrentPolicyContext,
  type UserProfile,
  type UserPermissions,
  ProductType,
} from './types';
import { getEmptyPolicyContext } from './constants';
import { lhrApiService } from './services/lhrApiService';
import { generateQuotePDF } from './utils/pdfGenerator';
import { AuthProvider, useAuth, type AuthPrincipal } from './auth';
import { resolveApiError } from './services/errorMessages';
import type {
  AppliedFacility,
  QuoteBindRequest,
  QuoteBindResponse,
  QuoteReferRequest,
  QuoteReferResponse,
} from '@cortex/domain';
import { cortexApi } from './services/cortexApi';

type ViewState = ProductType | 'admin' | 'history' | 'pipeline' | 'claims';

/**
 * Build a legacy `UserProfile` from the real `AuthPrincipal` so the
 * existing React tree (Sidebar, profile menu, the engine-injected
 * `meta.authenticatedUser`) keeps working without a tree-wide rewrite.
 *
 * Tear-out plan (PR #5): pass the AuthPrincipal directly to every
 * consumer that currently reads `UserProfile`, then delete this adapter
 * + the `UserProfile` type alias.
 */
const userProfileFromPrincipal = (p: AuthPrincipal): UserProfile => {
  const keys = p.permissions.keys;
  const permissions: UserPermissions = {
    customAuthorityLimit:
      p.permissions.authority.bindLimit !== null
        ? Number(p.permissions.authority.bindLimit)
        : undefined,
    canBindQuote: keys.includes('quotes.bind'),
    canAuthorizeReferral: keys.includes('quotes.refer'),
    canViewAllQuotes: keys.includes('quotes.read.all'),
  };
  return {
    id: p.user.id,
    name: p.user.displayName,
    email: p.user.email,
    companyId: p.user.entity.id,
    jobTitle: p.permissions.roles[0]?.displayName ?? '',
    role: p.legacyRole,
    status: 'Active',
    permissions,
    lastLogin: p.session.lastSeenAt,
    dateCreated: p.session.createdAt,
  };
};

const LDUApp: React.FC = () => {
  const auth = useAuth();
  const { showToast } = useToast();

  // ─── Authentication state ────────────────────────────────────────────
  // Two parallel tracks during the v1 → real-auth migration:
  //   - When `auth.state.status === 'authenticated'`, the principal
  //     comes from the real API (/auth/me).
  //   - When `auth.state.status === 'unconfigured'` (no VITE_API_BASE_URL),
  //     the legacy mock LoginScreen drives `localUser` directly.
  //
  // The two never coexist; the render branch below picks one. The
  // tear-out happens in PR #5 once mock mode is no longer required.
  const [localUser, setLocalUser] = useState<UserProfile | null>(null);
  const [localAuthenticated, setLocalAuthenticated] = useState(false);
  const realUser: UserProfile | null =
    auth.principal !== null ? userProfileFromPrincipal(auth.principal) : null;
  const activeUser: UserProfile | null = realUser ?? localUser;
  const isAuthenticated = auth.state.status === 'authenticated' || localAuthenticated;

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const [context, setContext] = useState<CurrentPolicyContext>(() => getEmptyPolicyContext());
  const [appliedFacility, setAppliedFacility] = useState<AppliedFacility | null>(null);

  const [isCalculating, setIsCalculating] = useState(false);
  const [view, setView] = useState<ViewState>(ProductType.CommercialPA);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  // Engine config must be loaded before the workbench (which reads it
  // synchronously) can render. Gate the authenticated UI on this.
  const [configReady, setConfigReady] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  const generationRef = useRef(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Real-auth bootstrap: seed quote context + load engine config when
  //     the principal lands. The mock-login path loads config separately;
  //     the Cloudflare Access path must do the same or the workbench's
  //     synchronous getConfig() call throws.
  useEffect(() => {
    if (auth.state.status !== 'authenticated') return;
    const u = userProfileFromPrincipal(auth.state.principal);
    setContext((prev) => ({
      ...prev,
      meta: { ...prev.meta, userRole: u.role, authenticatedUser: u },
    }));

    void (async () => {
      try {
        await lhrApiService.loadConfig();
        setConfigError(null);
        setConfigReady(true);
      } catch {
        setConfigError(
          'Could not load engine configuration. Please reload, or contact the platform team if this persists.',
        );
        showToast(
          'Could not load engine configuration. Ensure the API is running and VITE_API_BASE_URL is set.',
          'error',
        );
      }
    })();
  }, [auth.state, showToast]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── Mock-mode login path (only used when API is unconfigured) ───────
  const handleMockLogin = async (userProfile: UserProfile): Promise<void> => {
    setLocalUser(userProfile);
    setLocalAuthenticated(true);

    setContext((prev) => ({
      ...prev,
      meta: { ...prev.meta, userRole: userProfile.role, authenticatedUser: userProfile },
    }));

    try {
      await lhrApiService.loadConfig();
      setConfigError(null);
      setConfigReady(true);
    } catch {
      showToast(
        'Could not load engine configuration. Ensure the API is running and VITE_API_BASE_URL is set.',
        'error',
      );
    }

    showToast(`Welcome back, ${userProfile.name}`, 'success');
  };

  const handleLogout = useCallback(async (): Promise<void> => {
    if (auth.state.status === 'authenticated') {
      await auth.logout();
    } else {
      setLocalUser(null);
      setLocalAuthenticated(false);
      showToast('Logged out successfully', 'info');
    }
    setContext(getEmptyPolicyContext());
    setAppliedFacility(null);
    setShowProfileMenu(false);
    setConfigReady(false);
    setConfigError(null);
  }, [auth, showToast]);

  const updateContext = useCallback(
    (newContext: CurrentPolicyContext): void => {
      setContext(newContext);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      const currentGen = generationRef.current;

      debounceTimerRef.current = setTimeout(async () => {
        setIsCalculating(true);
        try {
          const result = await lhrApiService.calculatePremium(newContext);
          if (generationRef.current !== currentGen) return;

          const calculated = result.context;
          setAppliedFacility(result.appliedFacility);

          setContext((latestSnapshot) => {
            const apiCatMap = new Map(calculated.categories.map((c) => [c.id, c]));
            const mergedCategories = latestSnapshot.categories.map((currCat) => {
              const apiCat = apiCatMap.get(currCat.id);
              if (!apiCat) return currCat;
              const apiBenMap = new Map(apiCat.benefits.map((b) => [b.id, b]));
              const mergedBenefits = currCat.benefits.map((currBen) => {
                const apiBen = apiBenMap.get(currBen.id);
                if (!apiBen) return currBen;
                return {
                  ...currBen,
                  netPremium: apiBen.netPremium,
                  grossRate: apiBen.grossRate,
                  systemFactors: apiBen.systemFactors,
                };
              });
              return {
                ...currCat,
                benefits: mergedBenefits,
                systemBasisFactor: apiCat.systemBasisFactor,
              };
            });

            return {
              ...latestSnapshot,
              quote_meta: calculated.quote_meta,
              policy_parameters: calculated.policy_parameters,
              pricing_summary: calculated.pricing_summary,
              categories: mergedCategories,
            };
          });
        } catch (err) {
          if (generationRef.current === currentGen) {
            const resolved = resolveApiError(err);
            showToast(`${resolved.title}: ${resolved.message}`, resolved.tone);
            setAppliedFacility(null);
          }
        } finally {
          if (generationRef.current === currentGen) setIsCalculating(false);
        }
      }, 600);
    },
    [showToast],
  );

  const canBind = activeUser?.permissions.canBindQuote ?? false;
  const canRefer = activeUser?.permissions.canAuthorizeReferral ?? false;

  const handleBind = useCallback(async (req: QuoteBindRequest): Promise<QuoteBindResponse> => {
    const result = await cortexApi.quotes.bind(req);
    return result;
  }, []);

  const handleRefer = useCallback(async (req: QuoteReferRequest): Promise<QuoteReferResponse> => {
    const result = await cortexApi.quotes.refer(req);
    return result;
  }, []);

  const handleReset = (): void => {
    setShowResetConfirm(true);
  };

  const confirmReset = (): void => {
    generationRef.current += 1;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setIsCalculating(false);

    const freshContext = getEmptyPolicyContext();

    if (activeUser) {
      freshContext.meta.userRole = activeUser.role;
      freshContext.meta.authenticatedUser = activeUser;
    }

    setContext(freshContext);
    setAppliedFacility(null);
    setResetKey((prev) => prev + 1);
    setShowResetConfirm(false);
    showToast('New Quote Started', 'info');
  };

  const handleFinishQuote = async (): Promise<void> => {
    if (!context.client_profile.companyName) {
      showToast('Validation Error: Missing Client Info', 'error');
      return;
    }
    const ref = context.quote_meta.ref;
    if (!ref || ref === 'New Quote') {
      showToast('Calculate the quote first before finalising', 'error');
      return;
    }
    setIsCalculating(true);
    try {
      await cortexApi.quotes.saveStatus({ ref, status: 'Quoted' });
      const finalizedSnapshot = {
        ...context,
        quote_meta: { ...context.quote_meta, status: 'Quoted' as const },
      };
      setContext(finalizedSnapshot);
      showToast(`Quote ${ref} Finalized & Saved`, 'success');
      await generateQuotePDF(finalizedSnapshot);
    } catch {
      showToast('Failed to finalise quote', 'error');
    } finally {
      setIsCalculating(false);
    }
  };

  const loadHistoryItem = (historicalContext: CurrentPolicyContext): void => {
    generationRef.current += 1;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setIsCalculating(false);
    const restored = JSON.parse(JSON.stringify(historicalContext)) as CurrentPolicyContext;
    if (activeUser) {
      restored.meta.userRole = activeUser.role;
      restored.meta.authenticatedUser = activeUser;
    }
    setContext(restored);
    setAppliedFacility(null);
    setResetKey((prev) => prev + 1);
    setView(ProductType.CommercialPA);
    showToast(`Snapshot ${restored.quote_meta.ref} Loaded`, 'info');
  };

  // ─── Auth gating render ──────────────────────────────────────────────
  if (auth.state.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hr-grey">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-4 border-hr-pink border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">
            Resolving session…
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Real-API mode: render the Cloudflare login screen for anonymous /
    // error states. Mock mode (unconfigured): keep the legacy login.
    if (auth.state.status === 'unconfigured') {
      return <LoginScreen onLoginSuccess={handleMockLogin} />;
    }
    return <CloudflareLoginScreen />;
  }

  // ─── Engine-config gate ──────────────────────────────────────────────
  // The workbench reads engine config synchronously on first render, so
  // hold the authenticated UI until it has loaded (or surface an error).
  if (configError !== null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hr-grey p-8">
        <div className="text-center space-y-4 max-w-md">
          <h2 className="text-xl font-black text-hr-indigo">Engine configuration unavailable</h2>
          <p className="text-xs text-slate-500 leading-relaxed">{configError}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="bg-hr-pink hover:bg-hr-pink-hover text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-colors text-xs uppercase tracking-wider"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  if (!configReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hr-grey">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-4 border-hr-pink border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">
            Loading engine configuration…
          </p>
        </div>
      </div>
    );
  }

  const isPlaceholderView =
    view === ProductType.CorporatePA ||
    view === ProductType.FilmMediaPA ||
    view === ProductType.AviationPA ||
    view === ProductType.ShortPeriodPA ||
    view === ProductType.BusinessTravel ||
    view === ProductType.OnceOffTravel ||
    view === ProductType.InboundTravel ||
    view === ProductType.LocalTravel ||
    view === ProductType.MedicalEvacuation ||
    view === ProductType.KidnapRansom ||
    view === 'pipeline' ||
    view === 'claims';

  const placeholderTitle =
    view === 'pipeline' ? 'Unified Pipeline' : view === 'claims' ? 'Claims Workbench' : view;

  return (
    <div className="flex h-screen overflow-hidden bg-slate-100 font-sans text-slate-900">
      <Sidebar activeProduct={view} onSelect={setView} userRole={activeUser?.role} />

      <div className="flex-1 flex flex-col min-w-0 h-screen">
        <header className="h-16 shrink-0 bg-white border-b border-slate-200 flex items-center justify-between px-8 z-40 shadow-sm">
          <div className="flex items-center gap-4">
            <h2 className="text-sm font-black text-hr-indigo uppercase tracking-widest">
              {view === 'pipeline'
                ? 'Unified Pipeline'
                : view === 'claims'
                  ? 'Claims Workbench'
                  : view === 'admin'
                    ? 'Admin Console'
                    : view === 'history'
                      ? 'Quote History'
                      : view}
            </h2>
            <div className="h-4 w-px bg-slate-200"></div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <span className="text-micro font-bold text-slate-400 uppercase tracking-widest">
                System Online
              </span>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="relative" ref={profileMenuRef}>
              <button
                type="button"
                className="flex items-center gap-3 group"
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                aria-haspopup="menu"
                aria-expanded={showProfileMenu}
              >
                <div className="text-right">
                  <p className="text-xs font-bold text-slate-800 group-hover:text-hr-pink transition-colors">
                    {activeUser?.name}
                  </p>
                  <p className="text-micro font-medium text-slate-400 uppercase tracking-widest">
                    {activeUser?.role}
                  </p>
                </div>
                <div className="h-9 w-9 bg-hr-indigo rounded-xl flex items-center justify-center text-white font-bold text-xs shadow-soft group-hover:bg-hr-pink transition-all">
                  {activeUser?.name.charAt(0)}
                </div>
              </button>
              {showProfileMenu && (
                <div className="absolute right-0 mt-3 w-56 bg-white rounded-2xl shadow-2xl border border-slate-100 py-2 z-50 animate-fade-in-up origin-top-right">
                  <div className="px-4 py-3 border-b border-slate-50">
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">
                      Account Settings
                    </p>
                    <p className="text-xs font-bold text-slate-800">{activeUser?.email}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleLogout()}
                    className="w-full text-left px-4 py-3 text-xs text-rose-500 hover:bg-rose-50 font-bold flex items-center gap-3 transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto no-scrollbar">
          <div className="max-w-[1600px] mx-auto p-8">
            {view === ProductType.CommercialPA && (
              <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
                <div className="xl:col-span-3 space-y-8">
                  <CommercialPAWorkbench
                    key={resetKey}
                    context={context}
                    onUpdate={updateContext}
                    isCalculating={isCalculating}
                  />
                </div>
                <div className="xl:col-span-2">
                  <div className="sticky top-8 max-h-[calc(100vh-6rem)] overflow-y-auto no-scrollbar pb-8 space-y-6">
                    {appliedFacility !== null && <AppliedFacilityCard facility={appliedFacility} />}
                    {canBind && context.pricing_summary?.grossPremium > 0 && (
                      <BindQuotePanel
                        context={context as unknown as Record<string, unknown>}
                        appliedFacility={appliedFacility}
                        grossPremium={context.pricing_summary.grossPremium}
                        quoteRef={String(context.quote_meta?.ref ?? '')}
                        onBind={handleBind}
                        onNewQuote={handleReset}
                      />
                    )}
                    {!canBind && canRefer && context.pricing_summary?.grossPremium > 0 && (
                      <ReferQuotePanel
                        context={context as unknown as Record<string, unknown>}
                        appliedFacility={appliedFacility}
                        grossPremium={context.pricing_summary.grossPremium}
                        quoteRef={String(context.quote_meta?.ref ?? '')}
                        onRefer={handleRefer}
                        onNewQuote={handleReset}
                      />
                    )}
                    <PolicySchedule
                      context={context}
                      onUpdateParams={(p) => updateContext({ ...context, policy_parameters: p })}
                      onFinishQuote={handleFinishQuote}
                      onNewQuote={handleReset}
                      isCalculating={isCalculating}
                    />
                  </div>
                </div>
              </div>
            )}

            {view === 'admin' && <AdminModule />}
            {view === 'history' && (
              <QuoteHistory
                onLoad={loadHistoryItem}
                onBack={() => setView(ProductType.CommercialPA)}
              />
            )}

            {isPlaceholderView && <ModulePlaceholder title={placeholderTitle} />}
          </div>
        </main>

        {showResetConfirm && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in-up">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200">
              <div className="bg-hr-indigo px-6 py-4 flex justify-between items-center">
                <h3 className="text-white font-bold text-base">Start Fresh Quote</h3>
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(false)}
                  className="text-white/60 hover:text-white transition-colors"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="p-6">
                <p className="text-slate-600 mb-6">
                  Are you sure you want to start a fresh quote? This will clear all current data and
                  reset the form. This action cannot be undone.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowResetConfirm(false)}
                    className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmReset}
                    className="px-4 py-2 text-xs font-bold text-white bg-hr-pink hover:bg-hr-pink-hover rounded shadow-md transition-colors"
                  >
                    Start Fresh Quote
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <ToastProvider>
      <AuthProvider>
        <LDUApp />
      </AuthProvider>
    </ToastProvider>
  </QueryClientProvider>
);

export default App;
